import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Lock, Plus, RotateCcw, Rows3, Save, Trash2 } from "lucide-react";
import BackOffice from "@/components/account/BackOffice";
import ConsignmentSectionBar from "@/components/account/ConsignmentSectionBar";
import { Btn, PageHead, Section, TONES, Toggle, cellCls } from "@/components/account/Shell";
import api, { formatApiError } from "@/lib/api";
import { GreenButton } from "@/components/Buttons";

const TONE = TONES.listings;

/** One column set for every roll of film, so every number sits under the same heading. */
const COLUMNS = [
    { key: "on", label: "On", sub: "shoppers can pick it", w: "64px" },
    { key: "name", label: "Film name", sub: "what shoppers see in the builder", w: "minmax(170px,1.3fr)" },
    { key: "width_inches", label: "Film width", sub: "inches · the roll you run", w: "92px", num: true, step: "0.25" },
    { key: "printable_width_inches", label: "Printable width", sub: "inches · never wider than the roll", w: "100px", num: true, step: "0.25" },
    { key: "price_per_linear_foot", label: "Price a foot", sub: "$ per linear foot", w: "92px", num: true, step: "0.25" },
    { key: "min_length_inches", label: "Shortest sheet", sub: "inches · bills at this or more", w: "96px", num: true, step: "1" },
    { key: "max_length_inches", label: "Longest sheet", sub: "inches · builder stops here", w: "96px", num: true, step: "1" },
    { key: "min_order_amount", label: "Minimum order", sub: "$ · 0 = any size", w: "92px", num: true, step: "1" },
    { key: "min_spacing_inches", label: "Spacing", sub: "inches between designs", w: "84px", num: true, step: "0.25" },
    { key: "turnaround", label: "Turnaround", sub: "what you promise", w: "minmax(140px,1fr)" },
    { key: "note", label: "Counter note", sub: "peel, press temp, anything", w: "minmax(150px,1fr)" },
    { key: "actions", label: "Actions", sub: "save · remove", w: "150px" },
];
const NUM_KEYS = COLUMNS.filter((c) => c.num).map((c) => c.key);
const gridOf = { gridTemplateColumns: COLUMNS.map((c) => c.w).join(" ") };

const chip = "inline-flex items-center gap-1 whitespace-nowrap border px-1.5 py-px text-[11px] font-semibold uppercase";

function HeaderRow() {
    return (
        <div className="sticky top-0 z-10 grid items-end border-b-2 border-[#1a1a1a] bg-[#fafafa]" style={gridOf} data-testid="film-sheet-header">
            {COLUMNS.map((c) => (
                <div key={c.key} className="px-1.5 py-1.5" title={`${c.label} — ${c.sub}`} data-testid={`film-col-${c.key}`}>
                    <p className="text-[11px] font-bold uppercase leading-tight text-[#1a1a1a]">{c.label}</p>
                    <p className="text-[10px] leading-tight text-[#4a4a4a]">{c.sub}</p>
                </div>
            ))}
        </div>
    );
}

/** One roll of film. The default row is the shop's counter (or 13thav's numbers); the rest are extra rolls. */
function FilmRow({ film, isDefault, locked, busy, onSave, onDelete, onReset, onToggle, own, draft }) {
    const [form, setForm] = useState(film);
    useEffect(() => setForm(film), [film]);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const dirty = draft || COLUMNS.some((c) => !["on", "actions"].includes(c.key) && String(form[c.key] ?? "") !== String(film[c.key] ?? ""));
    const tid = isDefault ? "default" : film.id;

    const save = () => {
        const body = { name: form.name || "", turnaround: form.turnaround || "", note: form.note || "" };
        NUM_KEYS.forEach((k) => { if (form[k] !== "" && form[k] != null) body[k] = Number(form[k]); });
        onSave(body);
    };

    const cells = {
        on: (
            <div className="flex items-center px-1.5">
                {isDefault
                    ? <span className={`${chip} border-[#1a1a1a] bg-[#1a1a1a] text-white`} title="The film shoppers get unless they pick another" data-testid="film-default-chip">default</span>
                    : <Toggle on={form.active !== false} disabled={locked || busy || draft} testid={`film-on-${tid}`} onChange={(v) => onToggle(v)} />}
            </div>
        ),
        actions: (
            <div className="flex items-center justify-end gap-1 px-1.5 py-1">
                {locked ? (
                    <span className="whitespace-nowrap text-[11px] font-semibold uppercase text-[#b42318]" data-testid={`film-locked-${tid}`}>locked</span>
                ) : (
                    <>
                        <Btn onClick={save} disabled={!dirty || busy} tone="solid" testid={`film-save-${tid}`}><Save size={10} /> Save</Btn>
                        {isDefault && own && (
                            <button onClick={onReset} disabled={busy} title="Back to 13thav's numbers" aria-label="Back to 13thav's numbers" data-testid="film-reset-default"
                                className="flex h-6 w-6 items-center justify-center border border-[#d0d0d0] bg-white text-[#1a1a1a] hover:border-[#1a1a1a] disabled:opacity-40">
                                <RotateCcw size={12} />
                            </button>
                        )}
                        {!isDefault && (
                            <button onClick={onDelete} disabled={busy} title={draft ? "Discard" : "Remove this film"} aria-label="Remove this film" data-testid={`film-delete-${tid}`}
                                className="flex h-6 w-6 items-center justify-center border border-[#d0d0d0] bg-white text-[#b42318] hover:border-[#b42318] disabled:opacity-40">
                                <Trash2 size={12} />
                            </button>
                        )}
                    </>
                )}
            </div>
        ),
    };

    return (
        <div className={`grid min-h-[42px] items-center border-t border-[#d0d0d0] ${locked ? "border-l-4 border-l-[#b42318]" : "border-l-4 border-l-transparent"} ${draft ? "bg-[#fffaeb]" : form.active === false && !isDefault ? "bg-[#fafafa]" : ""}`}
            style={gridOf} data-testid={`film-row-${tid}`}>
            {COLUMNS.map((c) => (
                <div key={c.key} className="min-w-0">
                    {cells[c.key] || (
                        <div className="px-1.5">
                            <input type={c.num ? "number" : "text"} step={c.step} min={c.num ? "0" : undefined} value={form[c.key] ?? ""} onChange={set(c.key)}
                                disabled={locked} data-testid={`film-${c.key}-${tid}`} aria-label={c.label}
                                placeholder={c.key === "name" ? `${form.width_inches || 16.5}" film` : c.key === "turnaround" ? "Pressed and cut in 2 business days" : c.key === "note" ? "Cold peel · 300°F · 12 sec" : ""}
                                className={`${cellCls} w-full ${c.num ? "" : "text-left"} ${locked ? "bg-[#f5f5f5] text-[#4a4a4a]" : ""}`} />
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

/** Your films, one row each. Shops run different rolls — a second machine, a wider film — this is where a shop says so. */
export default function GangSheets() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [drafts, setDrafts] = useState([]);

    const load = () => api.get("/store/gangsheet")
        .then(({ data: d }) => setData(d))
        .catch((e) => setData({ error: formatApiError(e.response?.data?.detail) }));
    useEffect(() => { load(); }, []);

    const run = async (fn, msg) => {
        setBusy(true);
        try { const { data: d } = await fn(); setData(d); if (msg) toast.success(msg); return true; }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
        finally { setBusy(false); }
    };

    const saveDefault = (body) => run(() => api.put("/store/gangsheet", body), "Your default film is set.");
    const resetDefault = () => run(() => api.delete("/store/gangsheet"), "Back to 13thav's film and price.");
    const saveFilm = (id, body) => run(() => api.put(`/store/gangsheet/films/${id}`, body), "Film saved.");
    const toggleFilm = (f, on) => run(() => api.put(`/store/gangsheet/films/${f.id}`, { active: on }), on ? `${f.name} is on — shoppers can pick it.` : `${f.name} is off.`);
    const deleteFilm = (id) => run(() => api.delete(`/store/gangsheet/films/${id}`), "Film removed.");
    const addDraft = () => setDrafts((d) => [...d, { id: `draft-${Date.now()}`, ...data.config, name: "", active: true }]);
    const saveDraft = async (draftId, body) => {
        const ok = await run(() => api.post("/store/gangsheet/films", body), "Film added — it's on and shoppers can pick it.");
        if (ok) setDrafts((d) => d.filter((x) => x.id !== draftId));
    };

    if (!data) {
        return <BackOffice><div className="flex min-h-[40vh] items-center justify-center">
            <Loader2 className="animate-spin text-zinc-400" /></div></BackOffice>;
    }
    if (data.error) {
        return (
            <BackOffice>
                <div className="py-16 text-center" data-testid="gang-locked">
                    <Rows3 size={34} className="mx-auto text-zinc-400" />
                    <h1 className="mt-4 font-display text-2xl font-black text-zinc-900">The gang sheet counter</h1>
                    <p className="mt-2 text-zinc-600">{data.error}</p>
                    <Link to="/account/sell" className="mt-5 inline-block"><GreenButton>See the spaces</GreenButton></Link>
                </div>
            </BackOffice>
        );
    }

    const locked = !!data.consigned;
    const rate = Number(data.config.price_per_linear_foot || 0);
    const films = data.films || [];

    return (
        <BackOffice>
            <div data-testid="gang-sheets-page">
                <PageHead kicker="Products" tone={TONE} title="DTF gang sheet films" testid="gang-head"
                    sub="Every roll of film you run is one row. Shoppers build on your default film. List a second roll — another machine, a wider film — and they pick the roll in the builder; the sheet width, spacing and price a foot follow it."
                    right={
                        <div className="text-right">
                            <p className="font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-zinc-700">13thav default</p>
                            <p className="text-sm font-semibold text-zinc-900" data-testid="gang-mall-default">
                                {data.mall.width_inches}" film · {data.mall.printable_width_inches}" printable · ${Number(data.mall.price_per_linear_foot).toFixed(2)} a foot
                            </p>
                        </div>
                    } />

                {data.consignment && (
                    <div className="mt-3">
                        <ConsignmentSectionBar room="dtf" info={data.consignment} rate={data.consignment.rate}
                            maxMarkup={data.consignment.max_markup}
                            example={Number(data.mall?.price_per_linear_foot) || 15} onChanged={load} />
                    </div>
                )}
                {locked && (
                    <p className="mt-3 flex items-center gap-1.5 border-2 border-[#b42318] bg-[#fef3f2] px-2.5 py-2 text-[13px] font-semibold text-[#b42318]" data-testid="gang-consigned-note">
                        <Lock size={13} /> {data.lock_note} Set your percent over in the red bar above.
                    </p>
                )}
                {!data.designers && (
                    <p className="mt-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-zinc-800" data-testid="gang-no-designers">
                        <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
                        The live designers come with a Pro Shop. Your numbers save either way — they go live the day 13thav support switches your designers on.
                    </p>
                )}
                {!data.line_on && (
                    <p className="mt-3 rounded-lg border border-black/15 bg-black/[0.03] p-3 text-sm text-zinc-800" data-testid="gang-line-off">
                        Your gang sheet counter is currently switched OFF, so shoppers won't see it.
                        <Link to="/account/store#counters" className="ml-1.5 font-semibold underline">Turn it on</Link>
                    </p>
                )}

                <div className="mt-3">
                    <Section title={`${1 + films.length} ${films.length ? "films" : "film"}`} tone={TONE} pad={false} testid="film-sheet-section"
                        note={data.own ? "Running your own numbers on the default film" : "Default film uses 13thav's numbers until you change a cell and save"}
                        right={!locked && (
                            <Btn onClick={addDraft} disabled={busy} testid="film-add"><Plus size={10} /> Add a film</Btn>
                        )}>
                        <div className="overflow-x-auto">
                            <div className="min-w-[1240px]" data-testid="film-sheet">
                                <HeaderRow />
                                <FilmRow film={{ ...data.config, name: data.config.name || "" }} isDefault locked={locked} busy={busy} own={data.own}
                                    onSave={saveDefault} onReset={resetDefault} />
                                {films.map((f) => (
                                    <FilmRow key={f.id} film={f} locked={locked} busy={busy}
                                        onSave={(body) => saveFilm(f.id, body)} onDelete={() => deleteFilm(f.id)} onToggle={(on) => toggleFilm(f, on)} />
                                ))}
                                {drafts.map((d) => (
                                    <FilmRow key={d.id} film={d} draft busy={busy}
                                        onSave={(body) => saveDraft(d.id, body)} onDelete={() => setDrafts((x) => x.filter((y) => y.id !== d.id))} />
                                ))}
                            </div>
                        </div>
                    </Section>
                </div>

                <div className="mt-3 rounded-lg border border-black/10 bg-white px-3 py-2.5" data-testid="gang-example">
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">What a sheet costs on your default film</p>
                    <div className="mt-1.5 flex flex-wrap gap-x-6 gap-y-1">
                        {[24, 60, 120].map((len) => (
                            <p key={len} className="text-sm text-zinc-900">
                                <span className="font-semibold">{len}"</span> long · <span className="tabular-nums">${((rate * len) / 12).toFixed(2)}</span>
                            </p>
                        ))}
                    </div>
                </div>
            </div>
        </BackOffice>
    );
}
